import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const ReceiptEdit = ({ match }) => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [invoicedeatils, setInvoicedeatils] = useState([]);
    const [formData, setFormData] = useState({
        CompanyId: companyId,
        BillDate: '',
        BillNo: 0,
        CustomerId: '',
        PaymentType: 0,
        Amount: 0,
        Remarks: '',
        createdBy: userId,
        TotalBalAmt: 0,
    });

    useEffect(() => {
        fetchData('ReceiptApi', `/ReceiptData/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const invoiceData = response.response_data.PaymentAndReceipt;
                    setFormData({
                        ...invoiceData,
                        UpdatedBy: userId,
                    });
                    const updatedInvoicedetailsSorted = response.response_data.PaymentAndReceiptRentCollection;
                    setInvoicedeatils(updatedInvoicedetailsSorted);
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice Details:', error);
            });
    }, [id]);

    const submitData = async (e) => {
        setIsLoading(true);

        if (!formData.Amount || formData.Amount === 0) {
            setErrorMessage('Amount must be Greater than 0!');
            setIsLoading(false);
            return;
        }

        if (!invoicedeatils || invoicedeatils.length === 0) {
            setErrorMessage('Item Details are required!');
            setIsLoading(false);
            return;
        }

        const createData = {
            PaymentAndReceipt: { ...formData, UpdatedBy: userId },
            PaymentAndReceiptRentCollection: invoicedeatils,
        };

        updateData('ReceiptApi', `${id}`, createData, token)
            .then(() => {
                setSuccessMessage('Receipt Updated successfully!');
                setErrorMessage('');
                history.push('/ReceiptIndex');
            })
            .catch((error) => {
                console.error('Receipt Update error:', error);
                setErrorMessage('Failed to update receipt.');
                setIsLoading(false);
            });
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (name === 'Amount') {
            let newAmount = parseFloat(value);
            if (isNaN(newAmount)) newAmount = 0;

            const restrictedAmount = Math.min(newAmount, formData.TotalBalAmt);
            setFormData((prevData) => ({
                ...prevData,
                [name]: type === 'checkbox' ? checked : restrictedAmount,
            }));

            let remainingBalance = restrictedAmount;
            const updatedInvoicedeatils = invoicedeatils.map((invdetail) => {
                const amountForThisRow = Math.min(invdetail.SaleAmount, remainingBalance);
                remainingBalance -= amountForThisRow;
                const updatedAmount = Math.min(amountForThisRow, invdetail.SaleAmount);
                const balanceForThisRow = Math.max(invdetail.SaleAmount - updatedAmount, 0);
                return {
                    ...invdetail,
                    Amount: updatedAmount,
                    BalanceAmount: balanceForThisRow,
                };
            });
            setInvoicedeatils(updatedInvoicedeatils);
        } else if (name === 'PaymentType') {
            setFormData((prevData) => ({
                ...prevData,
                PaymentType: value,
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <Fragment>
            <div className="form-container">
                <div className="form-card">
                    {/* Header */}
                    <div className="form-header">
                        <h1 className="form-title">Edit Rent Receipt</h1>
                        <div className="form-actions">
                            <Link to="/ReceiptIndex" className="btn-back">
                                <ArrowBackIcon fontSize="small" />
                                Back to List
                            </Link>
                            <button className="btn-save" onClick={submitData} disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <>
                                        <SaveIcon fontSize="small" />
                                        Update
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    {errorMessage && <div className="message error">{errorMessage}</div>}
                    {successMessage && <div className="message success">{successMessage}</div>}

                    {/* Form Body */}
                    <div className="form-body">
                        {/* Row 1 */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Customer *</label>
                                <input
                                    className="form-input"
                                    value={formData.CustomerName || ''}
                                    type="text"
                                    name="CustomerName"
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Date *</label>
                                <input
                                    className="form-input"
                                    value={formatDateForInput(formData.BillDate)}
                                    type="date"
                                    name="BillDate"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Number</label>
                                <input
                                    className="form-input"
                                    value={formData.BillNo || ''}
                                    type="text"
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Remarks</label>
                                <input
                                    className="form-input"
                                    value={formData.Remarks || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter remarks"
                                    name="Remarks"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Amount *</label>
                                <input
                                    className="form-input"
                                    value={formData.Amount || 0}
                                    type="text"
                                    name="Amount"
                                    readOnly
                                    style={{ textAlign: 'right', backgroundColor: '#f8fafc' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Payment Type</label>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '42px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="1"
                                            checked={formData.PaymentType === 1 || formData.PaymentType === "1"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        Cash
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="2"
                                            checked={formData.PaymentType === 2 || formData.PaymentType === "2"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        Cheque
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="3"
                                            checked={formData.PaymentType === 3 || formData.PaymentType === "3"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        UPI
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Details Table */}
                        {invoicedeatils && invoicedeatils.length > 0 && (
                            <div className="form-section">
                                <h3 className="section-title">Rent Bill Details</h3>
                                <div className="table-container">
                                    <div className="table-wrapper">
                                        <table className="customer-table">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th style={{ textAlign: 'right' }}>Bill Amount</th>
                                                    <th style={{ textAlign: 'right' }}>Paid Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoicedeatils.map((invdetail, index) => (
                                                    <tr key={index}>
                                                        <td className="cell-name">{invdetail.RentDescription}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            ₹{parseFloat(invdetail.RentAmount || 0).toFixed(2)}
                                                        </td>
                                                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                                                            ₹{parseFloat(invdetail.Amount || 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <td style={{ fontWeight: '600', color: '#1e3a5f' }}>Total</td>
                                                    <td style={{ textAlign: 'right', fontWeight: '600' }}>
                                                        ₹{invoicedeatils.reduce((sum, inv) => sum + (parseFloat(inv.RentAmount) || 0), 0).toFixed(2)}
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                                                        ₹{parseFloat(formData.Amount || 0).toFixed(2)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ReceiptEdit;
